import React, { useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios"; // make sure axios is installed
import { base_url } from "../utils/axiosConfig";

const roleContent = {
  member: {
    title: "LifeOnPlus Member",
    sections: [
      {
        icon: "❤️",
        heading: "Complete Healthcare Access",
        text: "Get doctor consultations, lab tests, medicines, and wellness services in one platform. Enjoy exclusive discounts and digital health records.",
      },
    ],
  },
  channelpartner: {
    title: "Channel Partner",
    sections: [
      {
        icon: "🤝",
        heading: "Grow Your Business With Us",
        text: "Earn attractive commissions by promoting LifeOnPlus products and services. Get full marketing support and real-time earnings dashboard.",
      },
    ],
  },
  franchise: {
    title: "LifeOnPlus Franchise",
    sections: [
      {
        icon: "🏪",
        heading: "Own Your Healthcare Business",
        text: "Start your own franchise with complete setup support, training, and marketing assistance. Multiple income streams with strong brand backing.",
      },
    ],
  },
  channelpartnerplus: {
    title: "Channel Partner Plus",
    sections: [
      {
        icon: "⭐",
        heading: "Premium Partnership Benefits",
        text: "Enjoy higher commissions, priority support, and exclusive privileges. Get advanced tools to grow your business faster.",
      },
    ],
  },
  corporate: {
    title: "Corporate Partnership",
    sections: [
      {
        icon: "🏢",
        heading: "Employee Healthcare Solutions",
        text: "Provide complete healthcare benefits to your employees. Track wellness analytics and reduce absenteeism with our digital platform.",
      },
    ],
  },
  director: {
    title: "Director / Super Distributor",
    sections: [
      {
        icon: "👔",
        heading: "Lead the Distribution Network",
        text: "Manage partner teams and drive regional growth. Earn high leadership commissions with strategic business support.",
      },
    ],
  },
  vendor: {
    title: "Vendor Marketplace",
    sections: [
      {
        icon: "🛍️",
        heading: "Sell Your Products Online",
        text: "List and sell healthcare products to clinics, corporates, and customers. Enjoy secure payments and pan-India reach.",
      },
    ],
  },
  enduser: {
    title: "End User",
    sections: [
      {
        icon: "📱",
        heading: "All Healthcare in One App",
        text: "Easily book doctors, lab tests, medicines, and wellness services. Get discounts and priority access to healthcare providers.",
      },
    ],
  },
};

// Validation Schemas
const leadSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Name is too short")
    .required("Full Name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  phone: Yup.string()
    .matches(/^[6-9]\d{9}$/, "Enter valid 10-digit Indian mobile number")
    .required("Phone number is required"),
});

const otpSchema = Yup.object().shape({
  otp: Yup.string()
    .matches(/^\d{6}$/, "OTP must be 6 digits")
    .required("OTP is required"),
});

const RoleInfoModal = ({ show, handleClose, role }) => {
  const data = roleContent[role] || roleContent["member"];
  const [step, setStep] = useState(1); // 1 = Info, 2 = Form, 3 = OTP, 4 = PDF
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Map frontend role key → backend relatedTo value
  const getRelatedTo = (roleKey) => {
    const map = {
      member: "member",
      channelpartner: "channelPartner",
      channelpartnerplus: "channelPartnerPlus",
      franchise: "franchise",
      corporate: "corporate",
      director: "director",
      vendor: "vendor",
      enduser: "endUser",
    };
    return map[roleKey] || "member";
  };

  // ===================== SEND OTP =====================
  const sendOtp = async (values) => {
    setLoading(true);
    setError("");

    try {
      const payload = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        relatedTo: getRelatedTo(role),
      };

      const res = await axios.post(
        `${base_url}v1/user/sendOtpForBrochure`,
        payload,
      );

      // Success
      setFormData(values);
      setStep(3);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to send OTP. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // ===================== VERIFY OTP =====================
  const verifyOtp = async (values) => {
    setLoading(true);
    setError("");

    try {
      const payload = {
        email: formData.email,
        phone: formData.phone,
        otp: values.otp,
        relatedTo: getRelatedTo(role),
      };

      const res = await axios.post(
        `${base_url}v1/user/verifyOtpForBrochure`,
        payload,
      );

      // Success → go to download step
      setStep(4);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Invalid OTP. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // ===================== RESEND OTP =====================
  const handleResendOtp = async () => {
    if (!formData.email || !formData.phone) return;

    setLoading(true);
    setError("");

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        relatedTo: getRelatedTo(role),
      };

      await axios.post("/api/send-otp-for-brochure", payload);
      setError(""); // clear previous errors
      // Optional: show success toast
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to resend OTP. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setStep(1);
    setFormData({ name: "", email: "", phone: "" });
    setError("");
    handleClose();
  };

  return (
    <Modal show={show} onHide={resetModal} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ color: "#02BE0F", fontWeight: 600 }}>
          {step === 1 && data.title}
          {step === 2 && "Fill Your Details"}
          {step === 3 && "Verify OTP"}
          {step === 4 && "Download Brochure"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{ maxHeight: "70vh", overflowY: "auto", background: "#f9f9f9" }}
      >
        {/* STEP 1: Simple Introduction */}
        {step === 1 && (
          <div
            className="bg-white p-4 rounded-3"
            style={{ borderLeft: "5px solid #02BE0F" }}
          >
            {data.sections.map((section, index) => (
              <div key={index}>
                <h5 style={{ color: "#02BE0F", fontWeight: 600 }}>
                  <span style={{ marginRight: 8 }}>{section.icon}</span>
                  {section.heading}
                </h5>
                <p style={{ color: "#555", lineHeight: 1.7, marginBottom: 16 }}>
                  {section.text}
                </p>
              </div>
            ))}

            {/* Instruction / Call-to-action */}
            <div
              className="mt-3 p-3 rounded-3"
              style={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
              }}
            >
              <p
                style={{
                  color: "#166534",
                  marginBottom: 0,
                  fontSize: "0.95rem",
                  lineHeight: 1.6,
                }}
              >
                <strong>Want to know more?</strong> Fill the form to download
                the detailed brochure. Our team will also connect with you to
                guide you through the process and answer all your questions.
              </p>
            </div>
          </div>
        )}

        {/* STEP 2: Lead Form */}
        {step === 2 && (
          <div className="bg-white p-4 rounded-3">
            <Formik
              initialValues={{ name: "", email: "", phone: "" }}
              validationSchema={leadSchema}
              onSubmit={sendOtp}
            >
              {({ isSubmitting }) => (
                <FormikForm>
                  <Form.Group className="mb-3">
                    <Form.Label>Full Name *</Form.Label>
                    <Field
                      name="name"
                      className="form-control"
                      placeholder="Enter your full name"
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Email Address *</Form.Label>
                    <Field
                      name="email"
                      type="email"
                      className="form-control"
                      placeholder="Enter your email"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number *</Form.Label>
                    <Field
                      name="phone"
                      type="tel"
                      className="form-control"
                      placeholder="Enter 10-digit mobile number"
                    />
                    <ErrorMessage
                      name="phone"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </Form.Group>

                  {error && <p className="text-danger mb-3">{error}</p>}

                  <div className="d-flex justify-content-between mt-4">
                    <Button variant="secondary" onClick={() => setStep(1)}>
                      Back
                    </Button>
                    <Button
                      type="submit"
                      style={{ background: "#02BE0F", border: "none" }}
                      disabled={isSubmitting || loading}
                    >
                      {loading ? <Spinner size="sm" /> : "Send OTP"}
                    </Button>
                  </div>
                </FormikForm>
              )}
            </Formik>
          </div>
        )}

        {/* STEP 3: OTP Verification */}
        {step === 3 && (
          <div className="bg-white p-4 rounded-3">
            <p className="mb-4 text-muted">
              We have sent the <strong>same OTP</strong> to your Email and
              Mobile Number.
            </p>

            <Formik
              initialValues={{ otp: "" }}
              validationSchema={otpSchema}
              onSubmit={verifyOtp}
            >
              {({ isSubmitting }) => (
                <FormikForm>
                  <Form.Group className="mb-3">
                    <Form.Label>Enter OTP</Form.Label>
                    <Field
                      name="otp"
                      className="form-control text-center fs-4"
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                    />
                    <ErrorMessage
                      name="otp"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </Form.Group>

                  {error && <p className="text-danger mb-3">{error}</p>}

                  <div className="text-center mb-3">
                    <button
                      type="button"
                      className="btn btn-link text-decoration-none"
                      onClick={handleResendOtp}
                      disabled={loading}
                    >
                      {loading ? "Sending..." : "Resend OTP"}
                    </button>
                  </div>

                  <div className="d-flex justify-content-between">
                    <Button variant="secondary" onClick={() => setStep(2)}>
                      Back
                    </Button>
                    <Button
                      type="submit"
                      style={{ background: "#02BE0F", border: "none" }}
                      disabled={isSubmitting || loading}
                    >
                      {loading ? <Spinner size="sm" /> : "Verify OTP"}
                    </Button>
                  </div>
                </FormikForm>
              )}
            </Formik>
          </div>
        )}

        {/* STEP 4: PDF Download */}
        {step === 4 && (
          <div className="bg-white p-5 rounded-3 text-center">
            <div style={{ fontSize: "3.5rem" }} className="mb-4">
              📄
            </div>
            <h4 className="mb-3" style={{ color: "#02BE0F" }}>
              Verification Successful!
            </h4>
            <p className="text-muted mb-4">
              Your brochure is ready. Click below to download.
            </p>
            <a
              href={`/pdfs/brochure.pdf`}
              // href={`/pdfs/${role}-brochure.pdf`}
              download
              className="btn btn-lg px-5 text-white"
              style={{ background: "#02BE0F", border: "none" }}
            >
              Download PDF
            </a>
          </div>
        )}
      </Modal.Body>

      {step === 1 && (
        <Modal.Footer>
          <Button
            style={{ background: "#02BE0F", border: "none" }}
            onClick={() => setStep(2)}
          >
            Continue → Fill Details
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default RoleInfoModal;
